import React, { useEffect, useState } from "react";
import { api, ROLE_LABELS, professionalTitle, SPECIALIZATIONS_LIST } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import ComposeDialog from "@/components/ComposeDialog";
import { Search, Stethoscope, MessageSquarePlus, ShieldCheck, UserCircle2, Building2, Hash, MapPin, PlaneTakeoff, Phone, Mail, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import SynapseLoader from "@/components/SynapseLoader";

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [province, setProvince] = useState("all");
  const [city, setCity] = useState("all");
  const [specialization, setSpecialization] = useState("all");
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [initialRecipient, setInitialRecipient] = useState(null);
  const [profile, setProfile] = useState(null);

  const hasSearch = (q || "").trim().length > 0
    || (role && role !== "all")
    || (province && province !== "all")
    || (city && city !== "all")
    || (specialization && specialization !== "all");

  const load = async () => {
    // Nessuna ricerca attiva → non chiediamo elenco, mostriamo hint
    if (!hasSearch) { setContacts([]); return; }
    const params = {};
    if (q) params.q = q;
    if (role) params.role = role;
    if (province) params.province = province;
    if (city && city !== "all") params.city = city;
    if (specialization && specialization !== "all") params.specialization = specialization;
    const { data } = await api.get("/contacts", { params });
    setContacts(data);
  };

  useEffect(() => {
    api.get("/contacts/provinces").then(({ data }) => setProvinces(data)).catch(() => {});
    api.get("/contacts/cities").then(({ data }) => setCities(data)).catch(() => {});
    api.get("/contacts/specializations").then(({ data }) => setSpecs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, role, province, city, specialization]);

  const openCompose = (c) => {
    setInitialRecipient(c);
    setComposeOpen(true);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-emerald-600/80">Contatti</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">Rubrica</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Solo professionisti verificati. Ogni scheda mostra <strong className="text-slate-800">telefono, email, sito web e indirizzo</strong> per contattare rapidamente colleghi, laboratori e strutture.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:flex-wrap gap-3 mb-6 [&_input]:bg-white [&_input]:border-slate-200 [&_input]:text-slate-800 [&_input]:placeholder:text-slate-400 [&_button[role=combobox]]:bg-white [&_button[role=combobox]]:border-slate-200 [&_button[role=combobox]]:text-slate-800">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Nome, ragione sociale, specializzazione…"
                 className="pl-9 h-11" data-testid="contacts-search" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="md:w-52 h-11" data-testid="contacts-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            <SelectItem value="medico_medicina_generale">Medico di Medicina Generale</SelectItem>
            <SelectItem value="medico_specialista">Medico Specialista</SelectItem>
            <SelectItem value="laboratorio">Laboratorio</SelectItem>
            <SelectItem value="diagnostica">Struttura diagnostica</SelectItem>
            <SelectItem value="altro_professionista">Altro professionista</SelectItem>
          </SelectContent>
        </Select>
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="md:w-52 h-11" data-testid="contacts-city-filter">
            <SelectValue placeholder="Città" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">Tutte le città</SelectItem>
            {cities.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={specialization} onValueChange={setSpecialization}>
          <SelectTrigger className="md:w-52 h-11" data-testid="contacts-specialization-filter">
            <SelectValue placeholder="Specializzazione" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">Tutte le specializzazioni</SelectItem>
            {(specs.length > 0 ? specs : SPECIALIZATIONS_LIST).map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={province} onValueChange={setProvince}>
          <SelectTrigger className="md:w-40 h-11" data-testid="contacts-province-filter">
            <SelectValue placeholder="Ordine" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">Tutti gli Ordini</SelectItem>
            {provinces.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4 text-xs text-slate-500">
        {hasSearch ? (
          <>
            {contacts.length} professionist{contacts.length === 1 ? "a" : "i"} trovat{contacts.length === 1 ? "o" : "i"}
            {city !== "all" && <span> a <strong className="text-slate-800">{city}</strong></span>}
            {specialization !== "all" && <span> · <strong className="text-slate-800">{specialization}</strong></span>}
            {role !== "all" && <span> · filtrat{contacts.length === 1 ? "o" : "i"} per ruolo</span>}
          </>
        ) : (
          <span>Inizia a digitare un nome, oppure applica un filtro (ruolo, provincia, città, specializzazione) per vedere i professionisti verificati.</span>
        )}
      </div>

      {hasSearch && contacts.length === 0 && (
        <Card className="p-8 text-center text-slate-500 bg-white border-slate-200 backdrop-blur-xl">
          Nessun contatto verificato trovato per questa ricerca.
        </Card>
      )}

      {!hasSearch && (
        <Card className="p-10 md:p-14 text-center bg-gradient-to-br from-white to-slate-50 border-slate-200 rounded-2xl"
              data-testid="contacts-empty-hint">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-[#DBEAFE] to-[#D1FAE5] text-[#1E3A8A] flex items-center justify-center mb-3">
            <Search className="h-7 w-7" strokeWidth={2.2} />
          </div>
          <h3 className="font-heading text-lg font-bold text-slate-900">Rubrica pronta all'uso</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Per proteggere la privacy dei colleghi e velocizzare la ricerca, i nomi appaiono solo quando cerchi.
            Digita un cognome, oppure filtra per <strong className="text-slate-700">ruolo</strong>,
            <strong className="text-slate-700"> provincia</strong> o
            <strong className="text-slate-700"> specializzazione</strong>.
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {hasSearch && contacts.map((c) => {
          const displayName = c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
          const initials = (displayName || "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
          return (
            <Card key={c.id} data-testid={`contact-card-${c.id}`}
                  onClick={() => setProfile(c)}
                  className="p-3 md:p-5 bg-white border-slate-200 shadow-sm shadow-black/5 rounded-2xl hover:border-emerald-400/30 hover:bg-white transition-all cursor-pointer">
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 md:h-14 md:w-14 rounded-full overflow-hidden bg-gradient-to-br from-[#1E3A8A] to-[#10B981] text-white flex items-center justify-center font-heading font-bold flex-shrink-0 ring-2 ring-white/10">
                  {c.avatar_url ? (
                    <img src={c.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : (initials || <UserCircle2 className="h-8 w-8" />)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="font-semibold text-slate-900">{displayName}</div>
                    {c.verification_status === "verified" && (
                      <span title="Professionista verificato" data-testid={`verified-badge-${c.id}`}
                            className="inline-flex items-center gap-0.5 text-emerald-600">
                        <ShieldCheck className="h-4 w-4" strokeWidth={2.4} />
                      </span>
                    )}
                    {c.absence?.active && (
                      <span data-testid={`absence-badge-${c.id}`}
                            title={`In ferie fino al ${c.absence.until_date} — Sostituto: ${c.absence.substitute_name}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                        <PlaneTakeoff className="h-3 w-3" /> In ferie
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] md:text-xs font-bold uppercase tracking-wider text-emerald-600/90 mt-0.5">
                    {professionalTitle(c.profession || c.role, c.specialization)}
                  </div>
                  {c.academic_title && (
                    <div className="text-[11px] md:text-xs text-slate-600 italic mt-0.5 truncate"
                         title={c.academic_title}
                         data-testid={`contact-academic-title-${c.id}`}>
                      {c.academic_title}
                    </div>
                  )}
                  {(c.profession === "laboratorio" || c.profession === "diagnostica" || c.profession === "altro_professionista") && c.specialization && (
                    <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
                      <Stethoscope className="h-3 w-3 text-slate-500" />{c.specialization}
                    </div>
                  )}
                </div>
              </div>

              {c.bio && (
                <p className="mt-2 md:mt-3 text-xs text-slate-500 leading-relaxed line-clamp-2 md:line-clamp-3" title={c.bio}>
                  {c.bio}
                </p>
              )}

              {c.order_province && (
                <div className="text-[10px] md:text-[11px] text-slate-500 mt-2">
                  Ordine {c.order_province} · N° {c.order_number}
                </div>
              )}
              {(c.phone || c.email || c.website) && (
                <div className="mt-2 space-y-1 border-t border-slate-100 pt-2"
                     data-testid={`contact-quick-info-${c.id}`}>
                  {c.phone && (
                    <a href={`tel:${c.phone}`}
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`contact-phone-${c.id}`}
                       className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-emerald-700 transition-colors">
                      <Phone className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                      <span className="truncate">{c.phone}</span>
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`}
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`contact-email-${c.id}`}
                       className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-emerald-700 transition-colors">
                      <Mail className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                      <span className="truncate">{c.email}</span>
                    </a>
                  )}
                  {c.website && (
                    <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                       target="_blank" rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`contact-website-${c.id}`}
                       className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-emerald-700 transition-colors">
                      <Globe className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                      <span className="truncate">{c.website.replace(/^https?:\/\//, "")}</span>
                    </a>
                  )}
                </div>
              )}
              {c.locations && c.locations.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1"
                     data-testid={`contact-locations-${c.id}`}>
                  {c.locations.slice(0, 3).map((loc) => (
                    <span key={loc.id || `${loc.name}-${loc.city}`}
                          title={loc.address ? `${loc.name} · ${loc.address}` : loc.name}
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                      <MapPin className="h-2.5 w-2.5 text-[#1E3A8A]" />
                      <span className="truncate max-w-[140px]">{loc.city}</span>
                    </span>
                  ))}
                  {c.locations.length > 3 && (
                    <span className="text-[10px] text-slate-500 px-1.5 py-0.5">
                      +{c.locations.length - 3} sed{c.locations.length - 3 === 1 ? "e" : "i"}
                    </span>
                  )}
                </div>
              )}
              <Button variant="outline"
                      onClick={(e) => { e.stopPropagation(); openCompose(c); }}
                      data-testid={`contact-msg-${c.id}`}
                      className="mt-3 md:mt-4 w-full h-9 md:h-10 text-sm border-slate-200 bg-slate-50 text-slate-200 hover:bg-white/[0.10] hover:text-white">
                <MessageSquarePlus className="h-4 w-4 mr-2" /> <span className="md:hidden">Contatta</span><span className="hidden md:inline">Nuova comunicazione</span>
              </Button>
            </Card>
          );
        })}
      </div>

      <ComposeDialog open={composeOpen} onOpenChange={setComposeOpen}
                     initialRecipient={initialRecipient} />

      {/* Scheda professionista */}
      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="profile-dialog">
          {profile && (() => {
            const dn = profile.name || `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
            const initials2 = (dn || "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
            return (
              <>
                <DialogHeader className="sr-only">
                  <DialogTitle>Scheda professionista {dn}</DialogTitle>
                  <DialogDescription>Profilo di {dn}</DialogDescription>
                </DialogHeader>

                <div className="-mx-6 -mt-6 h-24 bg-gradient-to-r from-[#1E3A8A] via-[#1E40AF] to-[#3B82F6] rounded-t-lg" />
                <div className="flex items-end -mt-14 mb-3">
                  <div className="h-28 w-28 rounded-full overflow-hidden ring-4 ring-white bg-[#DBEAFE] text-[#1E3A8A] flex items-center justify-center font-heading font-extrabold text-4xl shadow-lg">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
                    ) : (initials2 || <UserCircle2 className="h-16 w-16" />)}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-heading text-2xl font-extrabold text-slate-900"
                      data-testid="profile-name">{dn}</h2>
                  {profile.verification_status === "verified" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200">
                      <ShieldCheck className="h-3.5 w-3.5" /> Verificato
                    </span>
                  )}
                  {profile.absence?.active && (
                    <span data-testid="profile-absence-badge"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                      <PlaneTakeoff className="h-3.5 w-3.5" /> In ferie
                    </span>
                  )}
                </div>
                {profile.absence?.active && (
                  <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="profile-absence-note">
                    Attualmente in ferie fino al <strong>{profile.absence.until_date}</strong>.
                    Le nuove richieste verranno gestite dal sostituto <strong>{profile.absence.substitute_name}</strong>.
                  </div>
                )}
                <div className="text-sm font-bold uppercase tracking-wider text-[#1E3A8A] mt-1">
                  {professionalTitle(profile.profession || profile.role, profile.specialization)}
                </div>
                {profile.academic_title && (
                  <div className="mt-1 text-sm text-slate-700 italic"
                       data-testid="profile-academic-title">
                    {profile.academic_title}
                  </div>
                )}

                {(profile.profession === "laboratorio" || profile.profession === "diagnostica" || profile.profession === "altro_professionista") && profile.specialization && (
                  <div className="flex items-center gap-2 text-sm text-slate-700 mt-3">
                    <Stethoscope className="h-4 w-4 text-slate-400" />
                    <span className="font-medium">{profile.specialization}</span>
                  </div>
                )}

                {profile.bio && (
                  <div className="mt-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                      Descrizione professionale
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
                       data-testid="profile-bio">{profile.bio}</p>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-0.5 flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> Ordine
                    </div>
                    <div className="text-sm text-slate-800">{profile.order_province || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-0.5 flex items-center gap-1">
                      <Hash className="h-3 w-3" /> N° iscrizione
                    </div>
                    <div className="text-sm text-slate-800 font-mono">{profile.order_number || "—"}</div>
                  </div>
                </div>

                {(profile.phone || profile.email || profile.website) && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
                      Contatti diretti
                    </div>
                    <div className="space-y-1.5" data-testid="profile-contact-info">
                      {profile.phone && (
                        <a href={`tel:${profile.phone}`}
                           data-testid="profile-phone"
                           className="flex items-center gap-2 text-sm text-slate-800 hover:text-emerald-700 transition-colors">
                          <Phone className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          <span className="truncate">{profile.phone}</span>
                        </a>
                      )}
                      {profile.email && (
                        <a href={`mailto:${profile.email}`}
                           data-testid="profile-email"
                           className="flex items-center gap-2 text-sm text-slate-800 hover:text-emerald-700 transition-colors">
                          <Mail className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          <span className="truncate">{profile.email}</span>
                        </a>
                      )}
                      {profile.website && (
                        <a href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`}
                           target="_blank" rel="noopener noreferrer"
                           data-testid="profile-website"
                           className="flex items-center gap-2 text-sm text-slate-800 hover:text-emerald-700 transition-colors">
                          <Globe className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          <span className="truncate">{profile.website.replace(/^https?:\/\//, "")}</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {profile.locations && profile.locations.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-[#1E3A8A]" />
                      Sed{profile.locations.length === 1 ? "e" : "i"} di lavoro
                    </div>
                    <ul className="space-y-2" data-testid="profile-locations">
                      {profile.locations.map((loc) => (
                        <li key={loc.id || `${loc.name}-${loc.city}`}
                            className="text-sm text-slate-800 leading-tight">
                          <div className="font-semibold">{loc.name}</div>
                          <div className="text-slate-500 text-xs">
                            {loc.address && <>{loc.address} · </>}{loc.city}
                          </div>
                          {(loc.phone || loc.email) && (
                            <div className="mt-1 flex flex-wrap gap-3 text-xs">
                              {loc.phone && (
                                <a href={`tel:${loc.phone}`}
                                   className="inline-flex items-center gap-1 text-slate-700 hover:text-emerald-700">
                                  <Phone className="h-3 w-3 text-emerald-600" />{loc.phone}
                                </a>
                              )}
                              {loc.email && (
                                <a href={`mailto:${loc.email}`}
                                   className="inline-flex items-center gap-1 text-slate-700 hover:text-emerald-700">
                                  <Mail className="h-3 w-3 text-emerald-600" />{loc.email}
                                </a>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={() => setProfile(null)} data-testid="profile-close">
                    Chiudi
                  </Button>
                  <Button onClick={() => { openCompose(profile); setProfile(null); }}
                          data-testid="profile-message"
                          className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
                    <MessageSquarePlus className="h-4 w-4 mr-2" /> Nuova comunicazione
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
